import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Clock3,
  Moon,
  Sun,
  Users,
  UtensilsCrossed,
} from "lucide-react";
import io from "socket.io-client";
import RhodeAssistant from "./RhodeAssistant";
import KdsServiceBoard from "./KdsServiceBoard";
import { KdsCompactClock, KdsFullscreenButton } from "./KdsCompactMeta";
import useRhodeDelayAlerts from "../hooks/useRhodeDelayAlerts";
import { showToast } from "../utils/toast";
import { getUiPerfProfile } from "../utils/perfProfile";
import { readLocalSnapshot, writeLocalSnapshot } from "../utils/localSnapshot";
import { apiFetch } from "../utils/apiClient";
import { computeAverageTicketPreparationMinutes } from "../utils/kdsMetrics";
import {
  buildKdsGroupKey,
  isUnacknowledgedSuite,
  partitionKdsOrdersByReadiness,
  sortKdsOrdersByPriority,
} from "../utils/kdsCoursePriority";
import { resolveOrderAggregator } from "../utils/aggregatorPresentation";
import {
  buildRhodePendingOrderPayload,
  buildRhodePendingOrderPayloads,
} from "../utils/rhodePendingOrders";

const API_BASE = "/api";
const PRODUCT_PLACEHOLDER = "/placeholder-menu.jpg";
const THEME_STORAGE_KEY = "moo_sync_kds_theme";
const TAB_SEQUENCE = ["pending", "preparing", "ready", "served"];
const CLICK_SOUND_SRC = "/assets/sounds/click-5.mp3";
const KDS_NOTIFICATION_SOUND_SRC = "/assets/sounds/message_tone.mp3";
const UI_PERF_PROFILE = getUiPerfProfile();
const KDS_POLL_INTERVAL_MS = UI_PERF_PROFILE.kdsPollIntervalMs;
const KDS_RELOAD_WINDOW_MS = Math.max(
  150,
  Math.min(400, Number(UI_PERF_PROFILE.kdsReloadDebounceMs) || 250),
);
const KITCHEN_ORDERS_SNAPSHOT_KEY = "moo_kitchen_orders_snapshot";
const KITCHEN_SETTINGS_SNAPSHOT_KEY = "moo_kitchen_settings_snapshot";

const fetchWithAuth = (url, options = {}) => {
  return apiFetch(url, options);
};

const parseJsonSafe = (raw, fallback = null) => {
  if (raw == null) return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const normalizeStatus = (status) => {
  const raw = (status || "").toString().toLowerCase().trim();
  if (!raw || raw === "new" || raw === "confirmed") return "pending";
  if (["pending", "preparing", "ready", "served"].includes(raw)) return raw;
  return "pending";
};

const getFirstName = (value) => {
  const raw = (value || "").toString().trim();
  if (!raw) return "N/A";
  const [firstToken] = raw.split(/\s+/);
  return firstToken || "N/A";
};

const parseOrderTimestamp = (value) => {
  if (!value) return new Date();
  const raw = String(value).trim();
  if (!raw) return new Date();
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const withZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized)
    ? normalized
    : `${normalized}Z`;
  const parsed = new Date(withZone);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const getOrderLocationLabel = (order) => {
  if (order?.isDelivery) {
    const label = order.customerName;
    return label ? `Livraison - ${label}` : "Livraison";
  }
  if (!order?.table) {
    return order?.originZoneName
      ? `Comptoir · À emporter · ${order.originZoneName}`
      : "Comptoir · À emporter";
  }
  return `Table ${order?.table || "N/A"}`;
};

const isPendingKdsItem = (item) =>
  ["pending", "confirmed"].includes(normalizeStatus(item?.status));

const resolveOrderStatus = (items = []) => {
  const statuses = items.map((item) => normalizeStatus(item.status));
  if (statuses.some((status) => ["pending", "confirmed"].includes(status)))
    return "pending";
  if (statuses.some((status) => status === "preparing")) return "preparing";
  if (statuses.some((status) => status === "ready")) return "ready";
  if (statuses.every((status) => status === "served")) return "served";
  return "pending";
};

const formatVariation = (item) => {
  const name = (item?.variation_name || "").toString().trim();
  const attrs = parseJsonSafe(item?.variation_attributes, null);
  if (!name && !attrs) return "";
  if (Array.isArray(attrs) && attrs.length) {
    return `${name || "Variation"} • ${attrs.map((a) => a?.value || a?.name || a).join(" · ")}`;
  }
  if (attrs && typeof attrs === "object") {
    return `${name || "Variation"} • ${Object.values(attrs).join(" · ")}`;
  }
  return name || "";
};

const statusLabel = {
  held: "À suivre",
  pending: "En attente",
  preparing: "En préparation",
  ready: "Prêt",
  served: "Servi",
};

const statusAccent = {
  pending: "border-amber-400/60 text-amber-200",
  preparing: "border-blue-400/60 text-blue-200",
  ready: "border-emerald-400/60 text-emerald-200",
  served: "border-slate-500/60 text-slate-300",
};

const statusAccentLight = {
  pending: "border-amber-400/70 text-amber-800 bg-amber-50",
  preparing: "border-red-300 text-red-700 bg-red-50",
  ready: "border-emerald-300 text-emerald-700 bg-emerald-50",
  served: "border-slate-300 text-slate-700 bg-slate-50",
};

const statusMeta = {
  pending: { label: "En attente", icon: Clock3 },
  preparing: { label: "En préparation", icon: UtensilsCrossed },
  ready: { label: "Prêt", icon: CheckCircle2 },
  served: { label: "Servi", icon: Users },
};

const orderPriority = { pending: 1, preparing: 2, ready: 3, served: 4 };

const sortOrdersByPriority = (list = []) =>
  sortKdsOrdersByPriority(list, orderPriority);

const KitchenInterface = ({
  destinationCode = "cuisine",
  destinationName = "Cuisine",
  destinationConfig = null,
  onDestinationConfigUpdated = null,
} = {}) => {
  const normalizedDestination = String(destinationCode || "cuisine")
    .trim()
    .toLowerCase();
  const isCustomDestination = normalizedDestination !== "cuisine";
  const ordersSnapshotKey = isCustomDestination
    ? `moo_kds_${normalizedDestination}_orders_snapshot`
    : KITCHEN_ORDERS_SNAPSHOT_KEY;
  const settingsSnapshotKey = isCustomDestination
    ? `moo_kds_${normalizedDestination}_settings_snapshot`
    : KITCHEN_SETTINGS_SNAPSHOT_KEY;
  const location = useLocation();
  const isSuperPath = location.pathname.startsWith("/superadmin");
  const [orders, setOrders] = useState(() =>
    (readLocalSnapshot(ordersSnapshotKey, null)?.orders || []).map((order) => ({
        ...order,
        timestamp: parseOrderTimestamp(order?.timestamp || order?.created_at),
      })),
  );
  const [activeTab, setActiveTab] = useState("pending");
  const [apiConnected, setApiConnected] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(null);
  const [kdsSettings, setKdsSettings] = useState(() =>
    readLocalSnapshot(settingsSnapshotKey, null),
  );
  const [kdsEnabled, setKdsEnabled] = useState(() =>
    isCustomDestination
      ? Boolean(Number(destinationConfig?.kds_enabled ?? 1))
      : Boolean(
          readLocalSnapshot(settingsSnapshotKey, null)?.kitchen_enabled ?? 1,
        ),
  );
  const [rhodeLocalEnabled, setRhodeLocalEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(
        `rhode_${normalizedDestination}_enabled_local`,
      );
      return saved === null ? true : saved === "1";
    } catch {
      return true;
    }
  });
  const [actionInFlight, setActionInFlight] = useState(null);
  const actionInFlightRef = useRef(null);
  const [rhodeConfigVersion, setRhodeConfigVersion] = useState(0);
  const [avgPrepMinutes, setAvgPrepMinutes] = useState(() => {
    const snapshot = readLocalSnapshot(ordersSnapshotKey, null);
    return Number.isFinite(snapshot?.avgPrepMinutes)
      ? snapshot.avgPrepMinutes
      : null;
  });
  const [themeMode, setThemeMode] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });
  const rhodeRef = useRef(null);
  const socketRef = useRef(null);
  const prevOrdersRef = useRef(new Map());
  const reloadTimerRef = useRef(null);
  const pollSettingsIntervalRef = useRef(null);
  const pollOrdersIntervalRef = useRef(null);
  const clickAudioRef = useRef(null);
  const notificationAudioRef = useRef(null);
  const rhodeConfigSnapshotRef = useRef("");
  const notificationDedupRef = useRef(new Map());
  const announcementsHydratedRef = useRef(false);
  const kdsEnabledRef = useRef(kdsEnabled);
  const loadOrdersRef = useRef(null);
  const ordersRef = useRef(orders);
  const loadSequenceRef = useRef(0);
  const loadInFlightRef = useRef(false);
  const loadPendingRef = useRef(false);
  const isDarkMode = themeMode !== "light";

  const rhodeConfig = useMemo(
    () => kdsSettings?.rhode_kitchen_config || null,
    [kdsSettings],
  );
  const rhodeGloballyEnabled = Boolean(kdsSettings?.rhode_kitchen_enabled ?? 1);
  const rhodeEnabled = rhodeGloballyEnabled && rhodeLocalEnabled;
  const themeStyles = useMemo(
    () =>
      isDarkMode
        ? {
            page: "bg-slate-950 text-white",
            topBar: "bg-slate-950/95 border-white/10",
            emptyCard: "border-white/10 bg-white/5 text-white/70",
            orderCard: "border-white/10 bg-slate-900/85",
            itemCard: "border-white/10 bg-white/5",
            subtleText: "text-white/70",
            delayedText: "text-orange-200",
            accentActive: "border-amber-400 bg-amber-400/10",
            accentInactive: "border-white/10 bg-white/5",
            statusPill: statusAccent,
            statusDone: "border-white/15 text-white/70",
            themeToggle:
              "border-white/10 bg-white/5 text-white/80 hover:border-amber-400/60",
          }
        : {
            page: "bg-gradient-to-b from-red-50 via-white to-amber-50 text-slate-900",
            topBar: "bg-white/95 border-amber-200",
            emptyCard: "border-amber-200 bg-white text-slate-600",
            orderCard: "border-amber-200 bg-white",
            itemCard: "border-amber-100 bg-amber-50/40",
            subtleText: "text-slate-700",
            delayedText: "text-red-700",
            accentActive: "border-red-300 bg-red-50",
            accentInactive: "border-amber-100 bg-white",
            statusPill: statusAccentLight,
            statusDone: "border-slate-300 text-slate-700 bg-slate-50",
            themeToggle:
              "border-amber-200 bg-white text-slate-700 hover:border-red-300 hover:bg-red-50",
          },
    [isDarkMode],
  );

  const kdsOrderPartitions = useMemo(
    () => partitionKdsOrdersByReadiness(orders),
    [orders],
  );
  const { productionOrders, rhodeAnnouncementOrders } = kdsOrderPartitions;
  useEffect(() => {
    rhodeRef.current?.syncPendingOrders?.(
      buildRhodePendingOrderPayloads(rhodeAnnouncementOrders),
    );
  }, [rhodeAnnouncementOrders]);
  const { getDelayStatus, isOrderDelayed, getElapsedMinutes } =
    useRhodeDelayAlerts(
      productionOrders,
      rhodeRef,
      normalizedDestination,
      rhodeConfig,
      rhodeEnabled,
    );

  const groupedOrders = (rows) => {
    const groups = new Map();
    for (const row of rows || []) {
      const displayNumber =
        (isCustomDestination
          ? row.internal_destination_number
          : row.internal_kitchen_number) ||
        row.order_number ||
        `KDS-${row.order_id || row.id}`;
      const key = buildKdsGroupKey(row, displayNumber);
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          displayNumber,
          orderId: row.order_id,
          orderNumber: row.order_number || null,
          courseId: Number(row.course_id) || null,
          courseNumber: Number(row.course_number) || 1,
          courseStatus: row.course_status || "fired",
          table: row.table_number,
          originZoneId: Number(row.origin_zone_id) || null,
          originZoneName: row.origin_zone_name || null,
          isDelivery: Boolean(row.is_delivery),
          context:
            String(row.order_type || "").toLowerCase() === "takeaway"
              ? "counter"
              : null,
          orderType: row.order_type || null,
          customerName: row.customer_name || null,
          deliveryMode: row.delivery_mode || null,
          deliveryMethod: row.delivery_method || null,
          serverName: getFirstName(row.server_name),
          created_at: row.created_at,
          timestamp: parseOrderTimestamp(row.course_fired_at || row.created_at),
          status: "pending",
          isRepeatTable: Boolean(row.is_repeat_table),
          dispatchEventId: Number(row.dispatch_event_id) || null,
          dispatchKind: row.dispatch_kind || null,
          dispatchActorName: getFirstName(row.dispatch_actor_name),
          dispatchEventCreatedAt: row.dispatch_event_created_at || null,
          items: [],
        });
      }
      const group = groups.get(key);
      group.items.push({
        id: row.id,
        name: row.product_name,
        quantity: Number(row.quantity) || 1,
        notes: row.special_instructions || "",
        variation_name: row.variation_name || null,
        variation_attributes: row.variation_attributes || null,
        extras: Array.isArray(row.extras) ? row.extras : [],
        image: row.image_path || row.image_url || PRODUCT_PLACEHOLDER,
        status: normalizeStatus(row.status),
        courseId: Number(row.course_id) || null,
        courseNumber: Number(row.course_number) || 1,
        courseStatus: row.course_status || "fired",
        dispatchEventId: Number(row.dispatch_event_id) || null,
        dispatchKind: row.dispatch_kind || null,
        dispatchActorName: getFirstName(row.dispatch_actor_name),
        dispatchEventCreatedAt: row.dispatch_event_created_at || null,
      });
      if (
        Number(row.dispatch_event_id) > Number(group.dispatchEventId || 0)
      ) {
        group.dispatchEventId = Number(row.dispatch_event_id);
        group.dispatchKind = row.dispatch_kind || null;
        group.dispatchActorName = getFirstName(row.dispatch_actor_name);
        group.dispatchEventCreatedAt = row.dispatch_event_created_at || null;
      }
      group.isRepeatTable = group.isRepeatTable || Boolean(row.is_repeat_table);
    }

    const now = Date.now();
    const list = Array.from(groups.values()).map((group) => {
        if (
          ["claim", "addition"].includes(
            String(group.dispatchKind || "").toLowerCase(),
          ) &&
          group.dispatchEventCreatedAt
        ) {
          group.timestamp = parseOrderTimestamp(group.dispatchEventCreatedAt);
        }
        group.status =
          group.courseStatus === "held"
            ? "held"
            : resolveOrderStatus(group.items);
        const elapsedMinutes = Math.floor(
          (now - group.timestamp.getTime()) / 60000,
        );
      const urgent = group.status === "pending" && elapsedMinutes >= 12;
      return {
        ...group,
        elapsedMinutes,
        urgent,
        isUnacknowledgedSuite: isUnacknowledgedSuite(group),
      };
    });

    return sortOrdersByPriority(list);
  };

  const playClickSound = useCallback(async () => {
    try {
      if (!clickAudioRef.current) {
        clickAudioRef.current = new Audio(CLICK_SOUND_SRC);
        clickAudioRef.current.preload = "auto";
      }
      clickAudioRef.current.currentTime = 0;
      await clickAudioRef.current.play();
    } catch {
      // ignore autoplay errors
    }
  }, []);

  const playKdsNotificationTone = useCallback(async () => {
    try {
      if (!notificationAudioRef.current) {
        notificationAudioRef.current = new Audio(KDS_NOTIFICATION_SOUND_SRC);
        notificationAudioRef.current.preload = "auto";
      }
      notificationAudioRef.current.currentTime = 0;
      await notificationAudioRef.current.play();
    } catch {
      // ignore autoplay errors
    }
  }, []);

  const bumpRhodeVersionIfNeeded = useCallback((settings) => {
    const signature = JSON.stringify({
      enabled: Number(settings?.rhode_kitchen_enabled ?? 1),
      config: settings?.rhode_kitchen_config || null,
    });
    if (signature === rhodeConfigSnapshotRef.current) return;
    rhodeConfigSnapshotRef.current = signature;
    setRhodeConfigVersion((prev) => prev + 1);
  }, []);

  const applyOrderItemPatch = (event) => {
    if (!event?.itemId) return;
    setOrders((current) => {
      let touched = false;
      const now = Date.now();
      const next = current
        .map((order) => {
          const itemIndex = order.items.findIndex(
            (item) => String(item.id) === String(event.itemId),
          );
          if (itemIndex === -1) return order;
          touched = true;
          const nextItems = [...order.items];
          const incomingStatus = normalizeStatus(event.status);
          if ((event.status || "").toLowerCase() === "cancelled") {
            nextItems.splice(itemIndex, 1);
          } else {
            nextItems[itemIndex] = {
              ...nextItems[itemIndex],
              status: incomingStatus,
              quantity:
                typeof event.quantity === "number"
                  ? Number(event.quantity) || nextItems[itemIndex].quantity
                  : nextItems[itemIndex].quantity,
            };
          }

          if (!nextItems.length) return null;

          const nextStatus = resolveOrderStatus(nextItems);

          const elapsedMinutes = Math.floor(
            (now - order.timestamp.getTime()) / 60000,
          );
          return {
            ...order,
            items: nextItems,
            status: nextStatus,
            elapsedMinutes,
            urgent: nextStatus === "pending" && elapsedMinutes >= 12,
          };
        })
        .filter(Boolean);

      if (!touched) return current;
      return sortOrdersByPriority(next);
    });
  };

  const announceNewOrders = (nextOrders) => {
    rhodeRef.current?.syncPendingOrders?.(
      buildRhodePendingOrderPayloads(nextOrders),
    );
    const prev = prevOrdersRef.current;
    const next = new Map(
      nextOrders.map((o) => [
        o.id,
        {
          status: o.status,
          pendingCount: o.items.filter((item) =>
            ["pending", "confirmed"].includes(normalizeStatus(item.status)),
          ).length,
          totalItems: o.items.length,
          dispatchEventId: Number(o.dispatchEventId) || null,
        },
      ]),
    );
    if (!announcementsHydratedRef.current) {
      prevOrdersRef.current = next;
      announcementsHydratedRef.current = true;
      return;
    }
    nextOrders.forEach((order) => {
      const previous = prev.get(order.id);
      const pendingCount = order.items.filter((item) =>
        ["pending", "confirmed"].includes(normalizeStatus(item.status)),
      ).length;
      const isNewDispatchEvent =
        ["claim", "addition"].includes(order.dispatchKind) &&
        Number(order.dispatchEventId) > 0 &&
        Number(order.dispatchEventId) !== Number(previous?.dispatchEventId || 0);
      const hasNewPendingItems =
        isNewDispatchEvent ||
        (order.status !== "held" &&
          pendingCount > 0 &&
          (!previous ||
            previous.status === "held" ||
            pendingCount > Number(previous.pendingCount || 0)));
      if (hasNewPendingItems) {
        const payload = buildRhodePendingOrderPayload(order);
        if (!payload) return;
        if (rhodeRef.current?.isEnabled()) {
          rhodeRef.current.speakNewOrder(payload);
        } else {
          const dedupeKey = `${order.id}:${pendingCount}`;
          const now = Date.now();
          const lastAt = notificationDedupRef.current.get(dedupeKey) || 0;
          if (now - lastAt >= 3500) {
            notificationDedupRef.current.set(dedupeKey, now);
            void playKdsNotificationTone();
          }
          for (const [key, ts] of notificationDedupRef.current.entries()) {
            if (now - ts > 15000) notificationDedupRef.current.delete(key);
          }
        }
      }
    });
    prevOrdersRef.current = next;
  };

  const loadOrders = async () => {
    if (!kdsEnabledRef.current) return;
    if (loadInFlightRef.current) {
      loadPendingRef.current = true;
      return;
    }
    const sequence = ++loadSequenceRef.current;
    loadInFlightRef.current = true;
    try {
      const endpoint = isCustomDestination
        ? `${API_BASE}/kds/${encodeURIComponent(normalizedDestination)}/orders`
        : `${API_BASE}/kitchen/orders`;
      const res = await fetchWithAuth(endpoint);
      if (!res.ok) throw new Error("API");
      const data = await res.json();
      const normalized = (Array.isArray(data) ? data : []).map((row) => ({
        ...row,
        status: normalizeStatus(row.status),
        extras: parseJsonSafe(row.extras, []),
      }));
      const grouped = groupedOrders(normalized);
      const nextAvgPrepMinutes =
        computeAverageTicketPreparationMinutes(normalized);
      if (sequence !== loadSequenceRef.current || !kdsEnabledRef.current)
        return;
      setOrders(grouped);
      setAvgPrepMinutes(nextAvgPrepMinutes);
      writeLocalSnapshot(ordersSnapshotKey, {
        orders: grouped,
        avgPrepMinutes: nextAvgPrepMinutes,
        updatedAt: Date.now(),
      });
      announceNewOrders(grouped);
      setApiConnected(true);
    } catch {
      if (sequence !== loadSequenceRef.current) return;
      const snapshot = readLocalSnapshot(ordersSnapshotKey, null);
      if (snapshot?.orders) {
        setOrders(
          Array.isArray(snapshot.orders)
            ? snapshot.orders.map((order) => ({
                ...order,
                timestamp: parseOrderTimestamp(
                  order?.timestamp || order?.created_at,
                ),
              }))
            : [],
        );
        setAvgPrepMinutes(
          Number.isFinite(snapshot.avgPrepMinutes)
            ? snapshot.avgPrepMinutes
            : null,
        );
      }
      setApiConnected(false);
    } finally {
      loadInFlightRef.current = false;
      if (loadPendingRef.current) {
        loadPendingRef.current = false;
        queueMicrotask(() => loadOrdersRef.current?.());
      }
    }
  };

  useEffect(() => {
    kdsEnabledRef.current = kdsEnabled;
    loadOrdersRef.current = loadOrders;
    ordersRef.current = orders;
  });

  const loadKdsSettings = async () => {
    try {
      const [res, destinationsRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/kds-settings`),
        isCustomDestination
          ? fetchWithAuth(`${API_BASE}/production-destinations`, {
              cache: "no-store",
            })
          : Promise.resolve(null),
      ]);
      if (!res.ok || (destinationsRes && !destinationsRes.ok)) return;
      const data = await res.json();
      const destinations = destinationsRes ? await destinationsRes.json() : [];
      const currentDestination = isCustomDestination
        ? (Array.isArray(destinations) ? destinations : []).find(
            (entry) => String(entry?.code) === normalizedDestination,
          )
        : null;
      setKdsSettings(data);
      writeLocalSnapshot(settingsSnapshotKey, data || null);
      bumpRhodeVersionIfNeeded(data);
      const enabled = isCustomDestination
        ? Boolean(
            Number(
              currentDestination?.kds_enabled ??
                destinationConfig?.kds_enabled ??
                1,
            ),
          )
        : Boolean(data?.kitchen_enabled ?? 1);
      kdsEnabledRef.current = enabled;
      setKdsEnabled(enabled);
      if (!enabled) {
        loadSequenceRef.current += 1;
        setOrders([]);
      }
    } catch {
      const snapshot = readLocalSnapshot(settingsSnapshotKey, null);
      if (snapshot) {
        setKdsSettings(snapshot);
        bumpRhodeVersionIfNeeded(snapshot);
        const enabled = isCustomDestination
          ? Boolean(Number(destinationConfig?.kds_enabled ?? 1))
          : Boolean(snapshot?.kitchen_enabled ?? 1);
        kdsEnabledRef.current = enabled;
        setKdsEnabled(enabled);
      }
    }
  };

  useEffect(() => {
    loadKdsSettings();
    loadOrdersRef.current?.();
    const stopPolling = () => {
      if (pollSettingsIntervalRef.current) {
        clearInterval(pollSettingsIntervalRef.current);
        pollSettingsIntervalRef.current = null;
      }
      if (pollOrdersIntervalRef.current) {
        clearInterval(pollOrdersIntervalRef.current);
        pollOrdersIntervalRef.current = null;
      }
    };
    const startPolling = () => {
      if (!pollSettingsIntervalRef.current) {
        pollSettingsIntervalRef.current = setInterval(
          loadKdsSettings,
          KDS_POLL_INTERVAL_MS,
        );
      }
      if (!pollOrdersIntervalRef.current) {
        pollOrdersIntervalRef.current = setInterval(
          () => loadOrdersRef.current?.(),
          KDS_POLL_INTERVAL_MS,
        );
      }
    };
    const handleVisibilityChange = () => {
      void loadKdsSettings();
      void loadOrdersRef.current?.();
      startPolling();
    };
    const handleFocus = () => {
      void loadOrdersRef.current?.();
      startPolling();
    };

    startPolling();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleFocus);
    }
    return () => {
      stopPolling();
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleFocus);
      }
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      if (socketRef.current) socketRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clickAudioRef.current) {
      clickAudioRef.current = new Audio(CLICK_SOUND_SRC);
      clickAudioRef.current.preload = "auto";
      clickAudioRef.current.load();
    }
    if (!notificationAudioRef.current) {
      notificationAudioRef.current = new Audio(KDS_NOTIFICATION_SOUND_SRC);
      notificationAudioRef.current.preload = "auto";
      notificationAudioRef.current.load();
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch {
      // ignore storage issues
    }
  }, [themeMode]);

  useEffect(() => {
    writeLocalSnapshot(ordersSnapshotKey, {
      orders,
      avgPrepMinutes,
      updatedAt: Date.now(),
    });
  }, [avgPrepMinutes, orders, ordersSnapshotKey]);

  useEffect(() => {
    if (!kdsSettings) return;
    writeLocalSnapshot(settingsSnapshotKey, kdsSettings);
  }, [kdsSettings, settingsSnapshotKey]);

  useEffect(() => {
    if (!kdsEnabled) return;
    const socket = io();
    socketRef.current = socket;
    const scheduleReload = () => {
      if (reloadTimerRef.current) return;
      reloadTimerRef.current = setTimeout(() => {
        reloadTimerRef.current = null;
        void loadOrdersRef.current?.();
      }, KDS_RELOAD_WINDOW_MS);
    };

    socket.on("connect", () => {
      const room = isCustomDestination
        ? `kds:${normalizedDestination}`
        : "cuisine";
      socket.emit("join-room", room, (result) => {
        const joined = Boolean(result?.ok);
        setRealtimeConnected(joined);
        if (!joined) {
          console.warn(
            `Impossible de rejoindre la salle ${room}:`,
            result?.code,
          );
        }
        scheduleReload();
      });
    });
    socket.on("disconnect", () => setRealtimeConnected(false));
    socket.on("connect_error", () => setRealtimeConnected(false));
    if (isCustomDestination) {
      socket.on("new-kds-order", (payload) => {
        if (String(payload?.destination || "") === normalizedDestination)
          scheduleReload();
      });
      socket.on("production-destinations-updated", () => {
        onDestinationConfigUpdated?.();
        void loadKdsSettings();
        scheduleReload();
      });
    } else {
      socket.on("new-kitchen-order", scheduleReload);
    }
    socket.on("tableCartChanged", scheduleReload);
    socket.on("table-server-transferred", (payload) => {
      const tableKey = String(payload?.tableNumber ?? payload?.tableId ?? "");
      const displayed = ordersRef.current.some(
        (order) =>
          String(order?.table ?? "") === tableKey &&
          !["ready", "served", "completed"].includes(
            String(order?.status || "").toLowerCase(),
          ),
      );
      if (!displayed || Number(payload?.unfinishedItemsCount || 0) <= 0) {
        scheduleReload();
        return;
      }
      const nextName = String(payload?.newOwnerName || "").trim();
      if (nextName) {
        setOrders((current) =>
          current.map((order) =>
            String(order?.table ?? "") === tableKey
              ? { ...order, serverName: getFirstName(nextName) }
              : order,
          ),
        );
      }
      rhodeRef.current?.speakServerTransfer?.(payload);
      scheduleReload();
    });
    socket.on("order-item-updated", (payload) => {
      const belongsToThisScreen = ordersRef.current.some((order) =>
        order.items.some((item) => String(item.id) === String(payload?.itemId)),
      );
      if (!belongsToThisScreen) return;
      applyOrderItemPatch(payload);
      if (
        (payload?.cancellation ||
          String(payload?.status || "").toLowerCase() === "cancelled") &&
        rhodeRef.current?.isEnabled()
      ) {
        rhodeRef.current.speakCancellation(payload);
      }
      scheduleReload();
    });
    socket.on("kds-items-rejected", (payload) => {
      const eventDestination = String(payload?.destination || "").toLowerCase();
      if (eventDestination === normalizedDestination) scheduleReload();
    });
    socket.on("kds-cleared", (payload) => {
      if (
        payload?.role ===
        (isCustomDestination ? normalizedDestination : "kitchen")
      ) {
        scheduleReload();
      }
    });
    socket.on("kdsSettingsUpdated", (nextSettings) => {
      setKdsSettings(nextSettings || null);
      writeLocalSnapshot(settingsSnapshotKey, nextSettings || null);
      bumpRhodeVersionIfNeeded(nextSettings || null);
      const enabled = isCustomDestination
        ? kdsEnabledRef.current
        : Boolean(nextSettings?.kitchen_enabled ?? 1);
      kdsEnabledRef.current = enabled;
      setKdsEnabled(enabled);
      if (!enabled) {
        loadSequenceRef.current += 1;
        setOrders([]);
      }
    });

    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kdsEnabled, isSuperPath]);

  const filteredOrders = useMemo(
    () => orders.filter((order) => order.status === activeTab),
    [orders, activeTab],
  );
  const upcomingCourses = kdsOrderPartitions.upcomingCourses;
  const stats = useMemo(
    () => ({
      pending: orders.filter((o) => o.status === "pending").length,
      preparing: orders.filter((o) => o.status === "preparing").length,
      ready: orders.filter((o) => o.status === "ready").length,
      served: orders.filter((o) => o.status === "served").length,
    }),
    [orders],
  );
  const pendingCount = stats.pending;
  const preparingCount = stats.preparing;
  const readyCount = stats.ready;
  const servedCount = stats.served;

  useEffect(() => {
    if (!["preparing", "ready", "served"].includes(activeTab)) return;
    if (filteredOrders.length > 0) return;
    const countByTab = {
      pending: pendingCount,
      preparing: preparingCount,
      ready: readyCount,
      served: servedCount,
    };
    const currentIndex = TAB_SEQUENCE.indexOf(activeTab);
    for (let i = currentIndex + 1; i < TAB_SEQUENCE.length; i += 1) {
      const key = TAB_SEQUENCE[i];
      if ((countByTab[key] || 0) > 0) {
        setActiveTab(key);
        return;
      }
    }
    if (pendingCount > 0) {
      setActiveTab("pending");
    }
  }, [
    activeTab,
    filteredOrders.length,
    pendingCount,
    preparingCount,
    readyCount,
    servedCount,
  ]);

  const setStatusForOrderItems = async (
    order,
    status,
    itemFilter = () => true,
  ) => {
    const ids = order.items
      .filter(itemFilter)
      .map((i) => i.id)
      .filter(Boolean);
    if (!ids.length) return;
    const response = await fetchWithAuth(
      `${API_BASE}/kds/orders/${order.orderId}/transition`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          department: normalizedDestination,
          status,
          item_ids: ids,
          client_request_id:
            typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `kds-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        }),
      },
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(
        response.status === 404 && !payload?.code
          ? "Le serveur MOO Sync n'est pas à jour. Redémarrez le serveur puis réessayez."
          : payload?.error || "Transition refusée",
      );
      error.code = payload?.code;
      throw error;
    }
    ids.forEach((itemId) => {
      applyOrderItemPatch({ itemId, orderId: order.orderId, status });
    });
    return payload;
  };

  const beginOrderAction = (orderId) => {
    if (actionInFlightRef.current) return false;
    actionInFlightRef.current = orderId;
    setActionInFlight(orderId);
    return true;
  };

  const endOrderAction = () => {
    actionInFlightRef.current = null;
    setActionInFlight(null);
  };

  const refreshAfterTransitionError = (error) => {
    if (
      [
        "KDS_TICKET_CHANGED",
        "KDS_STATUS_CONFLICT",
        "KDS_ATOMIC_UPDATE_FAILED",
      ].includes(String(error?.code || ""))
    ) {
      void loadOrdersRef.current?.();
      return true;
    }
    return false;
  };

  const acceptOrder = async (order) => {
    if (!beginOrderAction(order.id)) return;
    try {
      await setStatusForOrderItems(order, "preparing", isPendingKdsItem);
      showToast("success", `Commande ${order.id} acceptée`);
      void loadOrdersRef.current?.();
    } catch (error) {
      const refreshed = refreshAfterTransitionError(error);
      showToast(
        refreshed ? "info" : "error",
        refreshed
          ? "Le ticket a été actualisé avec son dernier état."
          : error?.message || "Erreur lors de l'acceptation",
      );
    } finally {
      endOrderAction();
    }
  };

  const markReady = async (order) => {
    if (!beginOrderAction(order.id)) return;
    try {
      await setStatusForOrderItems(
        order,
        "ready",
        (item) => normalizeStatus(item?.status) === "preparing",
      );
      showToast("success", `Commande ${order.id} prête`);
      void loadOrdersRef.current?.();
    } catch (error) {
      const refreshed = refreshAfterTransitionError(error);
      showToast(
        refreshed ? "info" : "error",
        refreshed
          ? "Le ticket a été actualisé avec son dernier état."
          : error?.message || "Erreur lors du passage en prêt",
      );
    } finally {
      endOrderAction();
    }
  };

  const markServed = async (order) => {
    if (!beginOrderAction(order.id)) return;
    try {
      await setStatusForOrderItems(
        order,
        "served",
        (item) => normalizeStatus(item?.status) === "ready",
      );
      showToast("success", `Commande ${order.id} servie`);
      void loadOrdersRef.current?.();
    } catch (error) {
      const refreshed = refreshAfterTransitionError(error);
      showToast(
        refreshed ? "info" : "error",
        refreshed
          ? "Le ticket a été actualisé avec son dernier état."
          : error?.message || "Erreur lors du marquage servi",
      );
    } finally {
      endOrderAction();
    }
  };

  const rejectOrder = async (order) => {
    const reason = window.prompt("Pourquoi refusez-vous cette préparation ?");
    if (!reason) return;
    if (!beginOrderAction(order.id)) return;
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/kds/orders/${order.orderId}/unavailable`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            department: normalizedDestination,
            reason,
            item_ids: order.items.map((item) => item.id).filter(Boolean),
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Refus impossible");
      showToast(
        "success",
        payload?.message ||
          "Articles retirés de cet écran. La caisse a été avertie.",
      );
      void loadOrdersRef.current?.();
    } catch (error) {
      showToast("error", error?.message || "Erreur lors du refus");
    } finally {
      endOrderAction();
    }
  };

  if (!kdsEnabled) {
    return (
      <div
        className={`moo-app-screen ${themeStyles.page} flex items-center justify-center`}
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold">
            Écran {destinationName} désactivé
          </h1>
          <p className={`${themeStyles.subtleText} mt-2`}>
            Active-le dans Gestion des Imprimantes & KDS.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`moo-app-screen ${themeStyles.page}`}>
      <div className="sticky top-0 z-20 border-b border-red-400/50 bg-[#b30606] rounded-b-[20px] sm:rounded-b-[25px] px-3 sm:px-6 py-3 sm:py-4">
        {realtimeConnected === false && (
          <div
            role="status"
            className="mb-3 rounded-xl border border-amber-200/60 bg-amber-950/70 px-3 py-2 text-sm font-medium text-amber-100"
          >
            Temps réel indisponible — actualisation automatique en cours.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] items-center gap-4">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-6 h-6 text-amber-300" />
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-white">
                Écran {destinationName}
              </h1>
              <p className="text-sm text-white/85">
                {apiConnected ? "API connectée" : "Mode local uniquement"}
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <KdsCompactClock averageMinutes={avgPrepMinutes} />
          </div>
          <div className="flex items-center gap-2 justify-self-end">
            <KdsFullscreenButton onBeforeToggle={playClickSound} />
            <button
              type="button"
              onClick={() => {
                void playClickSound();
                setThemeMode((prev) => (prev === "light" ? "dark" : "light"));
              }}
              className="rounded-2xl border border-white/20 bg-white/10 hover:bg-white/15 text-white px-3 py-2"
              title={isDarkMode ? "Mode clair" : "Mode sombre"}
            >
              {isDarkMode ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>
            <RhodeAssistant
              ref={rhodeRef}
              isEnabled={rhodeEnabled}
              onToggle={setRhodeLocalEnabled}
              onButtonClick={playClickSound}
              interface_type={normalizedDestination}
              interface_label={
                isCustomDestination ? destinationName : "la cuisine"
              }
              position="header"
              remoteConfig={rhodeConfig}
              configVersion={rhodeConfigVersion}
            />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 sm:gap-3 mt-2.5">
          {[
            ["pending", stats.pending],
            ["preparing", stats.preparing],
            ["ready", stats.ready],
            ["served", stats.served],
          ].map(([key, value]) => {
            const meta = statusMeta[key];
            const Icon = meta.icon;
            return (
            <button
              key={key}
              onClick={() => {
                void playClickSound();
                setActiveTab(key);
              }}
              className={`rounded-xl sm:rounded-2xl border px-2 sm:px-4 py-2 sm:py-3 text-left transition ${
                activeTab === key
                  ? "border-amber-400 bg-amber-400/10 text-white"
                  : "border-white/20 bg-white/5 text-white"
              }`}
            >
                <p className="text-[9px] sm:text-xs uppercase tracking-[0.08em] sm:tracking-[0.2em] text-white/80 truncate">
                  {meta.label}
                </p>
              <div className="mt-1 sm:mt-2 flex items-center justify-between">
                <p className="text-lg sm:text-2xl font-bold">{value}</p>
                <Icon className="hidden sm:block w-5 h-5 text-amber-400" />
              </div>
            </button>
            );
          })}
        </div>
      </div>

      <div className="p-3 sm:p-6">
        {filteredOrders.length === 0 &&
          upcomingCourses.length === 0 && (
          <div
            className={`rounded-3xl border p-8 text-center ${themeStyles.emptyCard}`}
          >
            Aucune commande dans cet onglet.
          </div>
          )}

        <KdsServiceBoard
          activeOrders={filteredOrders}
          upcomingCourses={upcomingCourses}
          subtleTextClass={themeStyles.subtleText}
          renderOrder={(order) => {
            const pendingItems = order.items.filter(isPendingKdsItem);
            const handledItems = order.items.filter(
              (item) => !isPendingKdsItem(item),
            );
            const showPendingOnly =
              order.status === "pending" && pendingItems.length > 0;
            const displayItems = showPendingOnly ? pendingItems : order.items;
            const hasMixedRepeatItems =
              order.isRepeatTable &&
              pendingItems.length > 0 &&
              handledItems.length > 0;
            const aggregatorMeta = resolveOrderAggregator(order);

            return (
            <article
              key={order.id}
              className={`rounded-2xl sm:rounded-3xl border p-4 sm:p-5 space-y-4 ${themeStyles.orderCard} ${hasMixedRepeatItems ? "border-amber-400/80 ring-1 ring-amber-400/40" : ""} shadow-lg sm:hover:shadow-xl transition-[box-shadow,border-color,background-color] duration-150 animate-fadeIn`}
            >
              <header className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold">
                        {order.displayNumber || order.id}
                      </h3>
                      {order.courseId && (
                        <span
                          className={`rounded-full border px-2 py-1 text-xs font-semibold ${
                            order.courseNumber > 1
                              ? "border-fuchsia-400/70 bg-fuchsia-400/10 text-fuchsia-200"
                              : "border-cyan-400/60 text-cyan-200"
                          }`}
                        >
                          {order.courseNumber > 1 ? "Suite · " : ""}Tour{" "}
                          {order.courseNumber}
                        </span>
                      )}
                    <span
                      className={`text-xs px-2 py-1 rounded-full border ${themeStyles.statusPill[order.status]}`}
                    >
                      {order.dispatchKind === "claim"
                        ? "Réclamé"
                        : statusLabel[order.status]}
                    </span>
                    {order.urgent && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full border ${
                          isDarkMode
                            ? "border-rose-400/70 text-rose-200 animate-pulse"
                            : "border-red-300 text-red-700 bg-red-50"
                        }`}
                      >
                        Urgent
                      </span>
                    )}
                    {order.isRepeatTable && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full border ${
                          isDarkMode
                            ? "border-orange-400/70 text-orange-200"
                            : "border-amber-300 text-amber-800 bg-amber-50"
                        }`}
                      >
                        Commande additionnelle
                      </span>
                    )}
                    {order.dispatchKind === "claim" && (
                      <span className="rounded-full border border-orange-400/70 bg-orange-400/10 px-2 py-1 text-xs font-semibold text-orange-200">
                        Réclamation prioritaire
                      </span>
                    )}
                    {order.dispatchKind === "addition" && (
                      <span className="rounded-full border border-fuchsia-400/70 bg-fuchsia-400/10 px-2 py-1 text-xs font-semibold text-fuchsia-200">
                        Rajout
                      </span>
                    )}
                  </div>
                    <div
                      className={`mt-2 text-sm flex flex-wrap gap-4 ${themeStyles.subtleText}`}
                    >
                    <span className="inline-flex items-center gap-1">
                      {aggregatorMeta ? (
                        <span
                          className="inline-flex h-7 min-w-[82px] items-center justify-center overflow-hidden rounded-lg px-2"
                            style={{
                              backgroundColor: aggregatorMeta.backgroundColor,
                            }}
                          title={`Commande ${aggregatorMeta.label}`}
                        >
                          <img
                            src={aggregatorMeta.logo}
                            alt={aggregatorMeta.label}
                            className="h-5 w-auto max-w-[68px] object-contain"
                            draggable="false"
                          />
                        </span>
                      ) : (
                        <>
                          <Users className="w-4 h-4" />
                          {getOrderLocationLabel(order)}
                        </>
                      )}
                    </span>
                    <span>Serveur: {getFirstName(order.serverName)}</span>
                      <span>
                        Depuis {getElapsedMinutes(order.timestamp)} min
                      </span>
                    {isOrderDelayed(order) && (
                        <span className={themeStyles.delayedText}>
                          {getDelayStatus(order).message}
                        </span>
                    )}
                  </div>
                </div>
              </header>

              <div className="space-y-3">
                {displayItems.map((item) => {
                  const variation = formatVariation(item);
                  return (
                      <div
                        key={item.id}
                        className={`rounded-2xl border p-3 ${themeStyles.itemCard} shadow-sm`}
                      >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-12 h-12 rounded-xl object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = PRODUCT_PLACEHOLDER;
                            }}
                          />
                          <div>
                            <p className="font-semibold">{item.name}</p>
                              {variation && (
                                <p
                                  className={`text-xs mt-1 ${themeStyles.subtleText}`}
                                >
                                  {variation}
                                </p>
                              )}
                              {Array.isArray(item.extras) &&
                                item.extras.length > 0 && (
                              <div
                                className={`mt-1 text-xs ${isDarkMode ? "text-[#fbbf24]" : "text-[#aa693e]"}`}
                              >
                                {item.extras
                                  .map((extra) => {
                                        const qty = Number(
                                          extra?.quantity || 1,
                                        );
                                    return `${qty}× ${extra?.name || "extra"}`;
                                  })
                                  .join(" • ")}
                              </div>
                            )}
                            {item.notes ? (
                                <p
                                  className={`text-xs mt-1 ${isDarkMode ? "text-orange-200" : "text-red-700"}`}
                                >
                                Note: {item.notes}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <span
                          className={`text-2xl font-bold ${isDarkMode ? "text-[#fbbf24]" : "text-[#aa693e]"}`}
                        >
                          {item.quantity}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {showPendingOnly && handledItems.length > 0 && (
                    <details
                      className={`rounded-2xl border px-3 py-2 text-sm ${
                    isDarkMode
                      ? "border-amber-400/40 bg-amber-400/10 text-amber-100"
                      : "border-amber-300 bg-amber-50 text-amber-900"
                      }`}
                    >
                    <summary className="cursor-pointer font-semibold">
                        Deja pris en charge ({handledItems.length} article
                        {handledItems.length > 1 ? "s" : ""})
                    </summary>
                      <div
                        className={`mt-2 space-y-1 ${themeStyles.subtleText}`}
                      >
                      {handledItems.slice(0, 4).map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between gap-3"
                          >
                          <span className="truncate">{item.name}</span>
                          <span className="shrink-0">x{item.quantity}</span>
                        </div>
                      ))}
                        {handledItems.length > 4 && (
                          <div>+{handledItems.length - 4} autre(s)</div>
                        )}
                    </div>
                  </details>
                )}
              </div>

              <div className="flex flex-wrap justify-center gap-3">
                {order.status === "pending" && (
                  <div className="w-full max-w-[560px] mx-auto grid grid-cols-2 gap-3">
                    <button
                      disabled={Boolean(actionInFlight)}
                      onClick={() => {
                        void playClickSound();
                        void acceptOrder(order);
                      }}
                      className="w-full rounded-xl px-3 sm:px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 bg-[#fbbf24] hover:bg-[#f59e0b] text-black disabled:opacity-50 disabled:cursor-wait"
                    >
                      <Clock3 className="w-4 h-4" />
                      Accepter
                    </button>
                    <button
                      disabled={Boolean(actionInFlight)}
                      onClick={() => {
                        void playClickSound();
                        void rejectOrder(order);
                      }}
                      className={`w-full rounded-xl border px-3 sm:px-6 py-3 text-sm sm:text-base font-semibold inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-wait ${
                        isDarkMode
                          ? "border-red-400/60 text-red-200"
                          : "border-red-300 text-red-700 bg-red-50"
                      }`}
                    >
                      Refuser
                    </button>
                  </div>
                )}
                {order.status === "preparing" && (
                  <button
                    disabled={Boolean(actionInFlight)}
                    onClick={() => {
                      void playClickSound();
                      void markReady(order);
                    }}
                    className={`rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-wait ${
                      isDarkMode
                        ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                        : "bg-amber-500 hover:bg-amber-400 text-slate-900"
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Marquer prêt
                  </button>
                )}
                {order.status === "ready" && (
                  <button
                    disabled={Boolean(actionInFlight)}
                    onClick={() => {
                      void playClickSound();
                      void markServed(order);
                    }}
                    className={`rounded-xl px-4 py-2 font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-wait ${
                      isDarkMode
                        ? "bg-violet-600 hover:bg-violet-500 text-white"
                        : "bg-slate-800 hover:bg-slate-700 text-white"
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Marquer servi
                  </button>
                )}
                {order.status === "served" && (
                    <div
                      className={`rounded-xl border px-4 py-2 text-sm ${themeStyles.statusDone}`}
                    >
                    Commande finalisée
                  </div>
                )}
              </div>
            </article>
            );
          }}
        />
      </div>
    </div>
  );
};

export default KitchenInterface;
